// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.
//
// Carbon display for the Alarm Event Management (AEM) API. Fetches a site's
// current alarm rows (GET /api/v1/sites/{site_id}/alarms) and renders them in a
// Carbon DataTable with a colored status tag per row. Supersedes the temporary
// AlarmApiProbe diagnostic strip.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableContainer,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  DataTableSkeleton,
  InlineNotification,
  Tag,
} from '@carbon/react';
import { listAlarms } from '../../services/alarmApiService';
import { EmptyState } from './ScreenHelpers';
import type { AlarmApiRecord, AlarmApiStatus } from '../../types';

type TagType =
  | 'red'
  | 'magenta'
  | 'purple'
  | 'blue'
  | 'cyan'
  | 'teal'
  | 'green'
  | 'gray'
  | 'cool-gray'
  | 'warm-gray'
  | 'high-contrast'
  | 'outline';

/** Status → Carbon Tag color + human label. Ordered most-urgent first. */
const STATUS_TAG: Record<AlarmApiStatus, { type: TagType; label: string }> = {
  active: { type: 'red', label: 'Active' },
  open: { type: 'magenta', label: 'Open' },
  acknowledged: { type: 'blue', label: 'Acknowledged' },
  shelved: { type: 'teal', label: 'Shelved' },
  suppressed: { type: 'purple', label: 'Suppressed' },
  cleared: { type: 'green', label: 'Cleared' },
  closed: { type: 'gray', label: 'Closed' },
};

/** Colored Carbon Tag for a single alarm status (null → em dash). */
export function AlarmStatusTag({ status }: { status: AlarmApiStatus | null }) {
  if (!status) return <>—</>;
  const cfg = STATUS_TAG[status] ?? { type: 'gray' as TagType, label: status };
  return (
    <Tag type={cfg.type} size="sm">
      {cfg.label}
    </Tag>
  );
}

const HEADERS = [
  { key: 'alarm_code', header: 'Alarm' },
  { key: 'alarm_status', header: 'Status' },
  { key: 'site_key', header: 'Site' },
  { key: 'equipment_key', header: 'Equipment' },
  { key: 'equipment_type', header: 'Type' },
  { key: 'point_key', header: 'Point' },
  { key: 'assigned_to', header: 'Assigned to' },
];

interface AlarmRow {
  id: string;
  alarm_code: string;
  alarm_status: string;
  site_key: string;
  equipment_key: string;
  equipment_type: string;
  point_key: string;
  assigned_to: string;
}

const toRow = (r: AlarmApiRecord, index: number): AlarmRow => ({
  id: r.alarm_code ?? `alarm-${index}`,
  alarm_code: r.alarm_code ?? '—',
  alarm_status: r.alarm_status ?? '',
  site_key: r.site_key ?? '—',
  equipment_key: r.equipment_key ?? '—',
  equipment_type: r.equipment_type ?? '—',
  point_key: r.point_key ?? '—',
  assigned_to: r.assigned_to ?? '—',
});

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; rows: AlarmRow[] };

export default function AlarmsTable({
  siteId,
  pageSize = 50,
}: {
  siteId: string;
  pageSize?: number;
}) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    listAlarms(siteId, { pageSize })
      .then(page => {
        if (!cancelled) setState({ status: 'ok', rows: page.data.map(toRow) });
      })
      .catch(err => {
        if (!cancelled) {
          setState({ status: 'error', message: (err as Error).message ?? 'Request failed' });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [siteId, pageSize]);

  if (state.status === 'loading') {
    return <DataTableSkeleton headers={HEADERS} columnCount={HEADERS.length} rowCount={5} />;
  }

  if (state.status === 'error') {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Couldn't load alarms"
        subtitle={state.message}
        hideCloseButton
      />
    );
  }

  if (state.rows.length === 0) {
    return <EmptyState big="No alarms for this site." />;
  }

  return (
    <DataTable rows={state.rows} headers={HEADERS} isSortable>
      {({ rows, headers, getHeaderProps, getRowProps, getTableProps }: any) => (
        <TableContainer title="Alarms" description={`${state.rows.length} current`}>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map((header: any) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row: any) => (
                <TableRow
                  {...getRowProps({ row })}
                  key={row.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/alarms/${row.id}`)}
                >
                  {row.cells.map((cell: any) => (
                    <TableCell key={cell.id}>
                      {cell.info.header === 'alarm_status' ? (
                        <AlarmStatusTag status={(cell.value || null) as AlarmApiStatus | null} />
                      ) : (
                        cell.value
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  );
}
